import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Upload, FileText, Loader2, CheckCircle2, AlertTriangle, XCircle,
  Landmark, Building2, UserSearch, Banknote, Printer, Download, IdCard,
} from 'lucide-react'
import { postJson } from '../../lib/api.js'
import { parseSsoReportFile } from '../../lib/ssoReport.js'
import { Card, fmt } from '../statistic/statShared.jsx'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'
import { useMe } from '../../lib/me.js'
import { canSetClaimStatus } from '../../lib/role.js'
// ⚠️ useEphemeralState, not useState: the hub UNMOUNTS this panel on a tab switch, and the
// PDF is parsed in the browser and never uploaded — `result` cannot be re-fetched. In-memory,
// survives SPA navigation and tab flips, resets on a hard reload, which is exactly right here.
import { useEphemeralState } from '../../lib/persist.js'
import PatientLink from '../../components/PatientLink.jsx'
import { FileDrop } from '../../kit'

/* -------------------------------------------------------------------------- */
/* นำเข้ารายงานตามเบิก ปกส. — upload a สำนักงานประกันสังคม report PDF (old layout or */
/* either 2026 one, see ssoReport.js), diff it                                  */
/* against our own claim lines, and reconcile in one confirmed action.          */
/*                                                                             */
/* THE POINT OF THIS PAGE is not the ticking. /reports/reimbursement already     */
/* lets someone tick a line รับแล้ว, which only ever answers "was this claim     */
/* paid?" for claims a human thought to look at. It cannot answer the question   */
/* that actually costs money — which treatments we performed and never got paid  */
/* for — because the CRM has never held the payer's side of the ledger.          */
/*                                                                             */
/* NOTHING IS WRITTEN UNTIL THE OPERATOR CONFIRMS. Upload → parse in the browser  */
/* → diff on the server (read-only) → review → confirm. The billing commit goes   */
/* per voucher group, because one เลขที่ใบสำคัญรับเงิน covers many lines and         */
/* carries its own transfer date; mixing them would stamp the wrong date.         */
/*                                                                             */
/* THE FILE NEVER LEAVES THE MACHINE. The picker hands JS a File, pdf.js reads it */
/* in page memory, and only the PARSED ROWS are posted. No upload, no storage.    */
/* -------------------------------------------------------------------------- */


const TH_MON = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
const thDate = (iso) => {
  if (!iso) return '—'
  const [y, m, d] = String(iso).split('-').map(Number)
  if (!y) return String(iso)
  return `${d} ${TH_MON[m - 1]} ${(y + 543) % 100}`
}

const Stat = ({ icon, label, value, sub, tone = 'slate' }) => {
  const tones = {
    slate: 'border-slate-200 bg-white text-slate-900',
    green: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    rose: 'border-rose-200 bg-rose-50 text-rose-800',
  }
  return (
    <div className={`rounded-xl border px-4 py-3 ${tones[tone]}`}>
      <div className="flex items-center gap-1.5 text-[12px] opacity-75">{icon}{label}</div>
      <div className="text-[22px] font-semibold leading-tight mt-0.5">{value}</div>
      {sub ? <div className="text-[11.5px] opacity-70 mt-0.5">{sub}</div> : null}
    </div>
  )
}

function Section({ icon, title, tone, count, amount, hint, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)
  const tones = {
    green: 'border-emerald-200 bg-emerald-50/60',
    amber: 'border-amber-200 bg-amber-50/60',
    rose: 'border-rose-200 bg-rose-50/60',
    slate: 'border-slate-200 bg-slate-50/60',
  }
  return (
    <section className={`rounded-2xl border ${tones[tone]}`}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-[14px] font-semibold text-slate-800">
          {icon}{title}
          <span className="rounded-full bg-white/80 border border-slate-200 px-2 py-0.5 text-[12px] font-medium">{fmt(count)}</span>
          {amount != null && count > 0 ? <span className="text-[12.5px] font-normal text-slate-600">{fmt(amount)} ฿</span> : null}
        </span>
        <span className="text-[12px] text-slate-500">{open ? 'ซ่อน' : 'ดู'}</span>
      </button>
      {hint ? <div className="px-4 pb-2 -mt-1 text-[12px] text-slate-600">{hint}</div> : null}
      {open && count > 0 ? <div className="px-4 pb-4 overflow-x-auto">{children}</div> : null}
    </section>
  )
}

const Th = ({ children, className = '' }) => (
  <th className={`text-left font-medium text-[12px] text-slate-500 pb-1.5 pr-3 whitespace-nowrap ${className}`}>{children}</th>
)
const Td = ({ children, className = '' }) => (
  <td className={`py-1.5 pr-3 text-[13px] text-slate-700 align-top ${className}`}>{children}</td>
)

/**
 * The report's name with the prefix dropped, bolding ONLY the field that actually
 * differs. Showing นาย/นาง/นางสาว here is noise — the CRM keeps the prefix in its own
 * column, so it is never what disagrees — and bolding the given name when it is the
 * surname that changed points the reader at the wrong word.
 */
const ReportName = ({ m }) => (
  <span>
    <span className={m.name_differs ? 'font-bold text-amber-700' : ''}>{m.pdf_name}</span>
    {m.pdf_surname ? <> <span className={m.surname_differs ? 'font-bold text-amber-700' : ''}>{m.pdf_surname}</span></> : null}
  </span>
)

const csvCell = (v) => {
  const s = String(v ?? '')
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

export default function SsoImport() {
  // ⚠️ This page had NO role check of any kind until 2026-09-14 — it never read `me`.
  // Its ยืนยัน button fires TWO writes: /api/billing/status (รับแล้ว) and
  // /api/sso/patient-backfill (เลขบัตรประชาชน + fullname). Both are now GM/Admin server-side.
  const me = useMe()
  const maySetStatus = canSetClaimStatus(me)
  // The NAME only, ephemeral like `parsed` — a tab flip used to keep the parsed report but
  // drop the file name, so the zone said "ลากไฟล์มาวาง" over a report already on screen.
  const [fileName, setFileName] = useEphemeralState('sso:fileName', '')
  const [parsing, setParsing] = useState(false)
  const [parsed, setParsed] = useEphemeralState('sso:parsed', null)
  const [parseError, setParseError] = useState('')

  const [checking, setChecking] = useState(false)
  const [result, setResult] = useEphemeralState('sso:result', null)
  const [checkError, setCheckError] = useState('')

  const [committing, setCommitting] = useState(false)
  const [commitMsg, setCommitMsg] = useState('')
  const [confirm, setConfirm] = useState(null)

  // Which patient repairs the operator has agreed to. เลขบัตร defaults ON — it fills a
  // blank or invalid field and destroys nothing. Renames default OFF — they overwrite a
  // name, and the report is not automatically the better source.
  const [fixIds, setFixIds] = useEphemeralState('sso:fixIds', () => new Set())
  const [fixNames, setFixNames] = useEphemeralState('sso:fixNames', () => new Set())

  useEffect(() => {
    if (!result) { setFixIds(new Set()); setFixNames(new Set()); return }
    setFixIds(new Set(result.matched.filter((m) => m.backfill_idcard).map((m) => m.seq)))
    setFixNames(new Set())
  }, [result])

  const toggle = (setFn) => (seq) => setFn((prev) => {
    const next = new Set(prev)
    if (next.has(seq)) next.delete(seq); else next.add(seq)
    return next
  })

  async function onPick(f) {
    setFileName(f?.name ?? ''); setParsed(null); setResult(null)
    setParseError(''); setCheckError(''); setCommitMsg('')
    if (!f) return
    if (!/\.pdf$/i.test(f.name) && f.type !== 'application/pdf') {
      setParseError('ต้องเป็นไฟล์ PDF เท่านั้น'); setFileName(''); return
    }
    setParsing(true)
    try {
      setParsed(await parseSsoReportFile(f))
    } catch (e) {
      setParseError(`อ่านไฟล์ไม่สำเร็จ: ${e?.message || e}`)
    } finally {
      setParsing(false)
    }
  }

  // ล้าง — everything derived from the file goes, including the ticked repairs.
  function reset() {
    setFileName(''); setParsed(null); setResult(null)
    setParseError(''); setCheckError(''); setCommitMsg('')
    setFixIds(new Set()); setFixNames(new Set()); setConfirm(null)
  }

  async function runCheck() {
    if (!parsed) return
    setChecking(true); setCheckError(''); setResult(null); setCommitMsg('')
    try {
      setResult(await postJson('/api/sso/reconcile', {
        ssoclinic: parsed.header.ssoclinic,
        service_site: parsed.header.service_site ?? '',
        start_date: parsed.header.start_date,
        end_date: parsed.header.end_date,
        rows: parsed.rows,
      }))
    } catch (e) {
      setCheckError(e?.body?.detail || e?.body?.error || e?.message || 'ตรวจสอบไม่สำเร็จ')
    } finally {
      setChecking(false)
    }
  }

  const t = result?.totals
  // Report total vs the CRM's own ปกส. total for the same window + branch set. null until the
  // check has run (or on an older Worker that does not return the amount). The footer ✓ only
  // proves the PDF was read completely — it says nothing about whether WE agree with it.
  const crmDiff = t?.crm_amount_in_scope == null ? null : Math.round((t.report_amount - t.crm_amount_in_scope) * 100) / 100
  const branchName = (id) => (result?.branches ?? []).find((b) => b.id === id)?.name ?? id
  const leftoverTotal = (result?.unpaid?.length ?? 0) + (result?.paid_elsewhere?.length ?? 0)
  const dominant = (result?.leftover_by_branch ?? []).find(
    (b) => leftoverTotal > 0 && b.count / leftoverTotal > 0.6 && b.received > b.count / 2,
  )

  /* ⚠️ BEING IN THE REPORT IS NOT THE SAME AS HAVING BEEN PAID.
   * A row with no เลขที่ใบสำคัญรับเงิน and no วันที่โอนเข้าบัญชี has been SUBMITTED and not
   * yet settled — the August file carries 26 of them. Marking those รับเงินแล้ว would
   * book money that has not arrived, and would also stamp an empty transfer date.
   * The test is the voucher + transfer date, not the status wording: no payment
   * reference means no payment, whatever the สถานะรับแจ้ง column happens to say. */
  const isPaidInReport = (m) => !!m.voucher && !!m.transfer_date

  /* ⚠️ A line that is `partial` or `rejected` is CLOSED, and this list is what gets
     committed. Filtering only on `!== 'received'` meant the next ปกส. report that
     happened to contain a written-off line would overwrite a human's decision with
     `received` — silently, and with no way to tell it had happened. */
  const SETTLED = new Set(['received', 'partial', 'rejected'])
  const matchedUnwritten = useMemo(
    () => (result?.matched ?? []).filter((m) => !SETTLED.has(String(m.advance_status ?? ''))),
    [result],
  )
  const toWrite = useMemo(() => matchedUnwritten.filter(isPaidInReport), [matchedUnwritten])
  const notYetPaid = useMemo(() => matchedUnwritten.filter((m) => !isPaidInReport(m)), [matchedUnwritten])

  const sumAmt = (arr) => arr.reduce((t, r) => t + (Number(r.amount) || 0), 0)

  const voucherGroups = useMemo(() => {
    const g = new Map()
    for (const m of toWrite) {
      const key = `${m.voucher}|${m.transfer_date}`
      if (!g.has(key)) g.set(key, { voucher: m.voucher, transfer_date: m.transfer_date, rows: [] })
      g.get(key).rows.push(m)
    }
    return [...g.values()]
      .map((x) => ({ ...x, amount: sumAmt(x.rows) }))
      .sort((a, b) => String(a.transfer_date).localeCompare(String(b.transfer_date)))
  }, [toWrite])

  // ยังไม่ได้รับเงิน is read a day at a time when someone rings ปกส., so it is grouped by
  // service date. The server already sorts date → branch → name inside each day.
  const unpaidByDate = useMemo(() => {
    const g = new Map()
    for (const u of result?.unpaid ?? []) {
      if (!g.has(u.service_date)) g.set(u.service_date, [])
      g.get(u.service_date).push(u)
    }
    return [...g.entries()]
  }, [result])

  const repairable = useMemo(() => (result?.matched ?? []).filter((m) => m.backfill_idcard || m.name_mismatch), [result])
  const idFixes = useMemo(() => repairable.filter((m) => m.backfill_idcard && fixIds.has(m.seq)), [repairable, fixIds])
  const nameFixes = useMemo(() => repairable.filter((m) => m.name_mismatch && fixNames.has(m.seq)), [repairable, fixNames])
  const nothingToDo = !voucherGroups.length && !idFixes.length && !nameFixes.length

  async function commit() {
    setConfirm(null)
    setCommitting(true); setCommitMsg('')
    const parts = []
    const failures = []
    try {
      let written = 0
      for (const g of voucherGroups) {
        try {
          const out = await postJson('/api/billing/status', {
            billing_ids: g.rows.map((r) => r.billing_id),
            status: 'received',
            claim_ref: g.voucher,
            // The payer's transfer date, not the wall clock.
            advance_received_at: g.transfer_date,
          })
          written += Number(out?.updated ?? g.rows.length)
        } catch (e) {
          failures.push(`${g.voucher}: ${e?.body?.detail || e?.message || 'ล้มเหลว'}`)
        }
      }
      if (voucherGroups.length) parts.push(`บันทึกรับเงิน ${fmt(written)} รายการ ${fmt(sumAmt(toWrite))} ฿`)

      if (idFixes.length || nameFixes.length) {
        try {
          const out = await postJson('/api/sso/patient-backfill', {
            idcards: idFixes.map((m) => ({ patient_id: m.patient_id, idcard: m.idcard })),
            names: nameFixes.map((m) => ({
              patient_id: m.patient_id, prefix: m.pdf_prefix, name: m.pdf_name, surname: m.pdf_surname,
            })),
          })
          if (out?.filled) parts.push(`เติมเลขบัตร ${fmt(out.filled)} ราย`)
          if (out?.renamed) parts.push(`แก้ชื่อ ${fmt(out.renamed)} ราย`)
          if (out?.rejected?.length) failures.push(`ข้าม ${out.rejected.length} ราย (${out.rejected[0]?.why ?? ''})`)
        } catch (e) {
          failures.push(`ประวัติคนไข้: ${e?.body?.detail || e?.message || 'ล้มเหลว'}`)
        }
      }

      setCommitMsg([parts.join(' · ') || 'ไม่มีอะไรถูกบันทึก', failures.length ? `⚠️ ${failures.join(' | ')}` : ''].filter(Boolean).join(' — '))
      await runCheck()
    } finally {
      setCommitting(false)
    }
  }

  /* The sheet is a SEPARATE ROUTE in a new tab, not window.print() here: this page
   * renders inside <Layout>, so printing it prints the sidebar and the nav as well —
   * which is what made the first attempt unusable. The result travels through
   * localStorage because the PDF was never uploaded (so the new tab cannot re-derive
   * it) and the payload is far too large for a query string. */
  function openPrintTab() {
    if (!result) return
    try {
      localStorage.setItem('sso-import:print', JSON.stringify({
        result,
        header: parsed?.header ?? null,
        savedAt: Date.now(),
        // The operator's current ticks travel with it, so the sheet is a record of what
        // is actually being recorded — not of every repair that was merely available.
        selection: { idcards: [...fixIds], names: [...fixNames] },
      }))
    } catch (e) {
      setCommitMsg(`เปิดหน้าพิมพ์ไม่สำเร็จ: ${e?.message || e}`)
      return
    }
    window.open('/reports/sso-import/print', '_blank', 'noopener')
  }

  function exportCsv() {
    if (!result) return
    const rows = [['กลุ่ม', 'ลำดับ', 'วันที่', 'HN', 'ชื่อในโปรแกรม', 'ชื่อในรายงาน', 'เลขบัตร', 'สาขา', 'ยอด', 'สถานะ', 'ใบสำคัญ', 'วันที่โอน']]
    for (const m of result.matched) rows.push(['ตรงกัน', m.seq, m.service_date, m.hn, m.crm_name, m.raw_name, m.idcard, branchName(m.branch_id), m.amount, m.advance_status === 'received' ? 'รับแล้ว' : 'รอบันทึก', m.voucher, m.transfer_date])
    for (const u of result.unpaid) rows.push(['ยังไม่ได้รับเงิน', '', u.service_date, u.hn, u.crm_name, '', u.idcard, branchName(u.branch_id), u.amount, 'ค้างรับ', '', ''])
    for (const u of (result.paid_elsewhere ?? [])) rows.push(['รับแล้วนอกรายงาน', '', u.service_date, u.hn, u.crm_name, '', u.idcard, branchName(u.branch_id), u.amount, 'รับแล้ว', u.claim_ref, ''])
    for (const u of result.unmatched) rows.push(['ไม่พบในโปรแกรม', u.seq, u.service_date, '', '', u.raw_name, u.idcard, '', u.amount, '', u.voucher, u.transfer_date])
    // BOM so Excel opens Thai correctly instead of mojibake.
    const blob = new Blob(['﻿' + rows.map((r) => r.map(csvCell).join(',')).join('\r\n')], { type: 'text/csv;charset=utf-8;' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `ตามเบิก-ปกส-${result.ssoclinic}-${result.start_date}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  return (
    /* Wrapper padding + breadcrumb now belong to ReimbursementHub.jsx. The
       breadcrumb pointed at /reports/reimbursement, which is the page this is
       now a tab OF — it would have been a link back to itself. */
    <div className="space-y-5">
      {/* ── 1. upload ── */}
      <div>
        <Card
          icon={<Upload size={17} />}
          title="นำเข้ารายงาน ปกส."
          subtitle="ดาวน์โหลด PDF จาก mms.sso.go.th → ลากไฟล์มาวาง → ระบบจะเทียบกับรายการในโปรแกรมให้ · ใช้ได้ทั้งรูปแบบเดิมและรูปแบบใหม่ (รายงานการจ่าย / รายงานการส่งเบิก)"
        >
          {/* ⚠️ HERE, not at the button. The PDF is parsed in the browser and never
              uploaded, so a non-GM who only learns she cannot save AFTER reconciling five
              tables loses all of it — `result` is in-memory state with nothing to reload. */}
          {!maySetStatus && (
            <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-900">
              ตรวจและพิมพ์รายงานได้ตามปกติ · การบันทึกรับเงินและแก้ประวัติคนไข้ต้องให้ GM หรือ Admin เป็นคนกด
            </div>
          )}
          <FileDrop kind="pdf" accept="application/pdf,.pdf" label="ลากไฟล์ PDF มาวางที่นี่"
            fileName={fileName} busy={parsing || checking || committing} onFile={onPick} onClear={reset} />

          {parseError ? (
            <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">{parseError}</div>
          ) : null}

          {parsed ? (
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat icon={<Building2 size={13} />} label="สถานพยาบาล" value={parsed.header.ssoclinic || '—'} sub={[parsed.header.clinicName, parsed.header.service_site_raw && `รับบริการที่ ${parsed.header.service_site_raw}`].filter(Boolean).join(' · ')} />
                <Stat icon={<FileText size={13} />} label="ช่วงวันที่" value={`${thDate(parsed.header.start_date)} – ${thDate(parsed.header.end_date)}`} />
                <Stat icon={<FileText size={13} />} label="จำนวนรายการ" value={fmt(parsed.rows.length)} />
                <Stat icon={<Banknote size={13} />} label="ยอดรวม" value={`${fmt(parsed.footer.parsed_total)} ฿`}
                  sub={parsed.footer.footer_total == null ? 'ไม่พบยอดรวมในไฟล์'
                    : !parsed.footer.reconciles ? `ไม่ตรงกับท้ายรายงาน (${fmt(parsed.footer.footer_total)})`
                      : crmDiff == null ? 'อ่านไฟล์ครบ · ยังไม่ได้เทียบกับระบบ'
                        : crmDiff === 0 ? 'ตรงกับยอดในระบบ ✓' : `ไม่ตรงกับยอดในระบบ (${fmt(t.crm_amount_in_scope)})`}
                  tone={parsed.footer.footer_total == null ? 'amber' : !parsed.footer.reconciles ? 'rose'
                    : crmDiff == null ? 'slate' : crmDiff === 0 ? 'green' : 'rose'} />
              </div>

              {parsed.footer.footer_total != null && !parsed.footer.reconciles ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">
                  <b>ยอดรวมไม่ตรง</b> — อ่านได้ {fmt(parsed.footer.parsed_total)} ฿ แต่ท้ายรายงานระบุ {fmt(parsed.footer.footer_total)} ฿
                  แปลว่าอ่านรายการไม่ครบ กรุณาแจ้งผู้ดูแลระบบ อย่าใช้ผลนี้บันทึก
                </div>
              ) : null}

              {parsed.warnings.length ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800">
                  <ul className="list-disc pl-4 space-y-0.5">{parsed.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                </div>
              ) : null}

              {/* ⚠️ REPORT_01 says จ่ายแล้ว with no voucher and no transfer date. The operator
                  should know before the check that this file cannot record the money. */}
              {parsed.header.has_voucher_column === false ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
                  <b>ไฟล์นี้คือรายงานการส่งเบิก บันทึกรับเงินไม่ได้</b> — ไม่มีเลขที่ใบสำคัญรับเงินและวันที่โอน แม้สถานะจะเขียนว่า “จ่ายแล้ว”
                  · ตรวจเทียบกับระบบได้ตามปกติ ถ้าจะบันทึกรับเงินให้อัปโหลด <b>รายงานการจ่าย</b> (REPORT_02) ของงวดเดียวกัน
                </div>
              ) : null}

              <div className="flex items-center gap-3">
                <button type="button"
                  disabled={checking || !parsed.rows.length || !parsed.header.ssoclinic || (parsed.footer.footer_total != null && !parsed.footer.reconciles)}
                  onClick={runCheck}
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-800 hover:bg-slate-900 disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 text-[13.5px] font-medium">
                  {checking ? <Loader2 size={15} className="animate-spin" /> : <UserSearch size={15} />} ตรวจสอบกับระบบ
                </button>
                <span className="text-[12.5px] text-slate-500">ขั้นตอนนี้ยังไม่บันทึกอะไรทั้งสิ้น</span>
              </div>
            </div>
          ) : null}

          {checkError ? (
            <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">{checkError}</div>
          ) : null}
        </Card>
      </div>

      {/* ── 2. the diff ── */}
      {result ? (
        <>
          <div className="space-y-5">
            <Card icon={<Landmark size={17} />} title="ผลการเทียบ"
              subtitle={`สาขาที่ใช้รหัส ${result.ssoclinic}: ${(result.branches ?? []).map((b) => b.name).join(' · ') || '—'}`}
              right={
                <div className="flex items-center gap-2">
                  <button type="button" onClick={exportCsv}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 px-3 py-1.5 text-[12.5px] font-medium text-slate-700">
                    <Download size={14} /> CSV
                  </button>
                  <button type="button" onClick={openPrintTab}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 px-3 py-1.5 text-[12.5px] font-medium text-slate-700">
                    <Printer size={14} /> พิมพ์ / บันทึก PDF
                  </button>
                </div>
              }
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat icon={<CheckCircle2 size={13} />} label="ตรงกัน" tone="green" value={fmt(t.matched)}
                  sub={`บัตร ปชช. ${fmt(t.by_tier.idcard)} · ชื่อ ${fmt(t.by_tier.fullname)}${t.cross_branch ? ` · ข้ามสาขา ${fmt(t.cross_branch)}` : ''}`} />
                <Stat icon={<Banknote size={13} />} label="ยังไม่ได้รับเงิน" tone={t.unpaid ? 'rose' : 'green'}
                  value={fmt(t.unpaid)} sub={`${fmt(t.unpaid_amount)} ฿ — ยังไม่เคยบันทึกรับเงิน`} />
                <Stat icon={<XCircle size={13} />} label="ไม่พบในโปรแกรม" tone={t.unmatched ? 'amber' : 'green'}
                  value={fmt(t.unmatched)} sub={`${fmt(t.unmatched_amount)} ฿`} />
                <Stat icon={<AlertTriangle size={13} />} label="ต้องเลือกเอง" tone={t.ambiguous ? 'amber' : 'green'}
                  value={fmt(t.ambiguous)} sub="เข้าได้หลายรายการ" />
              </div>
              {crmDiff != null ? (
                <div className={`mt-3 rounded-xl border px-4 py-2.5 text-[13px] ${crmDiff === 0
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-rose-200 bg-rose-50 text-rose-800'}`}>
                  {crmDiff === 0 ? <><b>ยอดตรงกับระบบ ✓</b> — </> : <><b>ไม่ตรงกับยอดในระบบ</b> — </>}
                  รายงาน ปกส. {fmt(t.report_amount)} ฿ ({fmt(t.report_rows)} รายการ) · ในโปรแกรม {fmt(t.crm_amount_in_scope)} ฿
                  ({fmt(t.crm_lines_in_scope)} รายการ)
                  {crmDiff !== 0 ? <> · ต่าง <b>{fmt(Math.abs(crmDiff))} ฿</b>{' '}
                    ({crmDiff > 0 ? 'รายงานมากกว่าโปรแกรม — ดู "ไม่พบในโปรแกรม"' : 'โปรแกรมมากกว่ารายงาน — ดู "ยังไม่ได้รับเงิน"'})</> : null}
                </div>
              ) : null}
              <div className="mt-3 text-[12.5px] text-slate-600">
                รายงาน {fmt(t.report_rows)} รายการ ({fmt(t.report_amount)} ฿) · ในโปรแกรมช่วงนี้ {fmt(t.crm_lines_in_scope)} รายการ
                (ทุกสาขา {fmt(t.crm_lines_all_branches)}) · รับเงินแล้ว {fmt(t.already_received)} · รอบันทึก <b>{fmt(t.to_write)}</b>
              </div>
            </Card>

            {dominant ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
                <b>ตรวจสอบรหัสสถานพยาบาลก่อน</b> — รายการที่เหลือ {fmt(dominant.count)} จาก {fmt(leftoverTotal)} รายการ
                เป็นของสาขา <b>{branchName(dominant.branch_id)}</b> และ {fmt(dominant.received)} รายการในนั้นบันทึกว่ารับเงินแล้ว
                แปลว่าสาขานี้น่าจะยื่นเบิกคนละรหัส/คนละรายงาน ไม่ใช่เงินที่หายไป
                — ถ้าใช่ ให้ลบ <code className="font-mono">ssoclinic</code> ของสาขานี้ออก แล้วอัปโหลดใหม่
              </div>
            ) : null}

            <Section icon={<Banknote size={15} className="text-rose-600" />}
              title="ยังไม่ได้รับเงิน — ทำแล้วแต่ยังไม่เคยบันทึกรับเงิน"
              tone={result.unpaid.length ? 'rose' : 'slate'} count={result.unpaid.length} amount={t.unpaid_amount} defaultOpen
              hint="รายการที่คลินิกทำและตั้งเบิกไว้ แต่ไม่อยู่ในรายงานงวดนี้ และไม่เคยถูกบันทึกว่ารับเงิน — นี่คือเงินที่ต้องตามกับ ปกส.">
              <table className="w-full">
                <thead><tr><Th>HN</Th><Th>ชื่อ</Th><Th>สาขา</Th><Th>ยอด</Th><Th>สถานะในระบบ</Th></tr></thead>
                {unpaidByDate.map(([date, rows]) => (
                  <tbody key={date}>
                    <tr className="bg-rose-100/70">
                      <td colSpan={5} className="py-1 px-2 text-[12.5px] font-semibold text-rose-900">
                        {thDate(date)}
                        <span className="font-normal text-rose-700/80"> · {fmt(rows.length)} รายการ · {fmt(rows.reduce((t, r) => t + r.amount, 0))} ฿</span>
                      </td>
                    </tr>
                    {rows.map((u) => (
                      <tr key={u.billing_id} className="border-t border-rose-100">
                        <Td><PatientLink patientId={u.patient_id} icon>{u.hn || '—'}</PatientLink></Td><Td><PatientLink patientId={u.patient_id}>{u.crm_name || '—'}</PatientLink></Td>
                        <Td>{branchName(u.branch_id)}</Td><Td>{fmt(u.amount)}</Td><Td>ค้างรับ</Td>
                      </tr>
                    ))}
                  </tbody>
                ))}
              </table>
            </Section>

            <Section icon={<CheckCircle2 size={15} className="text-slate-500" />}
              title="รับเงินแล้ว แต่ไม่อยู่ในรายงานงวดนี้" tone="slate"
              count={(result.paid_elsewhere ?? []).length} amount={t.paid_elsewhere_amount}
              hint="เคยบันทึกรับเงินไว้แล้วจากรายงานงวดอื่นหรือสาขาที่ใช้รหัสเดียวกัน — ไม่ใช่เงินที่หายไป ไม่ต้องทำอะไร">
              <table className="w-full">
                <thead><tr><Th>วันที่</Th><Th>HN</Th><Th>ชื่อ</Th><Th>สาขา</Th><Th>ยอด</Th><Th>ใบสำคัญ</Th></tr></thead>
                <tbody>
                  {(result.paid_elsewhere ?? []).map((u) => (
                    <tr key={u.billing_id} className="border-t border-slate-100">
                      <Td>{thDate(u.service_date)}</Td><Td><PatientLink patientId={u.patient_id} icon>{u.hn || '—'}</PatientLink></Td><Td><PatientLink patientId={u.patient_id}>{u.crm_name || '—'}</PatientLink></Td>
                      <Td>{branchName(u.branch_id)}</Td><Td>{fmt(u.amount)}</Td>
                      <Td className="font-mono text-[11.5px]">{u.claim_ref || '—'}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>

            <Section icon={<XCircle size={15} className="text-amber-600" />}
              title="ไม่พบในโปรแกรม — อยู่ในรายงานแต่ไม่มีรายการตั้งเบิก"
              tone="amber" count={result.unmatched.length} amount={t.unmatched_amount}
              hint="ปกส. จ่ายมาแล้ว แต่ในโปรแกรมไม่มีรายการนี้ — อาจคีย์ไม่ครบ หรือคนไข้ยังไม่มีประวัติ">
              <table className="w-full">
                <thead><tr><Th>#</Th><Th>วันที่</Th><Th>เลขบัตร</Th><Th>ชื่อในรายงาน</Th><Th>ยอด</Th><Th>มีคนไข้ในระบบ?</Th></tr></thead>
                <tbody>
                  {result.unmatched.map((u) => (
                    <tr key={u.seq} className="border-t border-amber-100">
                      <Td>{u.seq}</Td><Td>{thDate(u.service_date)}</Td>
                      <Td className="font-mono text-[12px]">{u.idcard}</Td><Td>{u.raw_name}</Td><Td>{fmt(u.amount)}</Td>
                      <Td>{u.patient_known ? 'มี — แต่ไม่มีรายการวันนั้น' : 'ไม่มีประวัติ'}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>

            <Section icon={<AlertTriangle size={15} className="text-amber-600" />}
              title="ต้องเลือกเอง — เข้าได้หลายรายการ" tone="amber" count={result.ambiguous.length}
              hint="ระบบไม่เดาให้ เพราะเดาผิดคือบันทึกว่ารับเงินผิดรายการ">
              <table className="w-full">
                <thead><tr><Th>#</Th><Th>วันที่</Th><Th>ชื่อในรายงาน</Th><Th>ตัวเลือก</Th></tr></thead>
                <tbody>
                  {result.ambiguous.map((a) => (
                    <tr key={a.seq} className="border-t border-amber-100">
                      <Td>{a.seq}</Td><Td>{thDate(a.service_date)}</Td><Td>{a.raw_name}</Td>
                      <Td>{a.candidates.map((c) => `${c.hn} ${c.crm_name}`).join(' | ')}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>

            <Section icon={<CheckCircle2 size={15} className="text-emerald-600" />} title="ตรงกัน"
              tone="green" count={result.matched.length} amount={t.matched_amount}
              hint="จับคู่ด้วยเลขบัตรประชาชนเป็นหลัก ถ้าไม่มีจึงใช้ชื่อ-สกุล">
              <table className="w-full">
                <thead><tr><Th>#</Th><Th>วันที่</Th><Th>HN</Th><Th>ชื่อ</Th><Th>จับคู่ด้วย</Th><Th>ใบสำคัญ</Th><Th>สถานะ</Th></tr></thead>
                <tbody>
                  {result.matched.map((m) => (
                    <tr key={m.seq} className="border-t border-emerald-100">
                      <Td>{m.seq}</Td><Td>{thDate(m.service_date)}</Td>
                      <Td><PatientLink patientId={m.patient_id} icon>{m.hn || '—'}</PatientLink></Td>
                      <Td><PatientLink patientId={m.patient_id}>{m.crm_name}</PatientLink>{m.name_mismatch ? <span className="text-[11.5px]"> · ในรายงาน: <ReportName m={m} /></span> : null}</Td>
                      <Td>
                        {m.match_tier === 'idcard' ? 'เลขบัตร' : <span className="text-amber-700">ชื่อ-สกุล</span>}
                        {m.cross_branch ? <span className="text-amber-700"> · ข้ามสาขา</span> : null}
                      </Td>
                      <Td className="font-mono text-[11.5px]">{m.voucher}</Td>
                      <Td>{m.advance_status === 'received' ? 'รับแล้ว' : <b className="text-slate-900">รอบันทึก</b>}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>

            {/* ── 3. patient-record repairs ── */}
            {repairable.length ? (
              <Card icon={<IdCard size={17} />} title="แก้ประวัติคนไข้จากรายงาน"
                subtitle="ทำพร้อมกับการบันทึกรับเงิน — เดือนถัดไปจะจับคู่ได้แม่นขึ้น">
                <div className="text-[12.5px] text-slate-600 mb-3">
                  <b>เติมเลขบัตร</b> ติ๊กไว้ให้อัตโนมัติ เพราะเป็นการเติมช่องที่ว่างอยู่ ไม่ได้ลบอะไร ·
                  {' '}<b>แก้ชื่อ</b> ไม่ติ๊กให้ เพราะเป็นการ<u>ทับ</u>ชื่อเดิม ต้องเลือกเอง (ระบบจะบันทึกชื่อเดิมไว้ในโน้ตคนไข้เสมอ)
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr><Th>HN</Th><Th>ชื่อในโปรแกรม</Th><Th>ชื่อในรายงาน</Th><Th>เลขบัตร</Th><Th className="text-center">เติมเลขบัตร</Th><Th className="text-center">แก้ชื่อ</Th></tr></thead>
                    <tbody>
                      {repairable.map((m) => (
                        <tr key={m.seq} className="border-t border-slate-100">
                          <Td>{m.hn || '—'}</Td>
                          <Td>{m.crm_name || '—'}</Td>
                          <Td>
                            {m.name_mismatch
                              ? <ReportName m={m} />
                              : <span className="text-slate-400">เหมือนกัน</span>}
                          </Td>
                          <Td className="font-mono text-[11.5px]">
                            {m.backfill_idcard
                              ? <>{m.crm_idcard ? <span className="text-rose-600 line-through">{m.crm_idcard}</span> : <span className="text-slate-400">ว่าง</span>} → {m.idcard}</>
                              : <span className="text-slate-400">ตรงกัน</span>}
                          </Td>
                          <Td className="text-center">
                            {m.backfill_idcard
                              ? <input type="checkbox" checked={fixIds.has(m.seq)} onChange={() => toggle(setFixIds)(m.seq)} className="w-4 h-4 accent-emerald-600" />
                              : <span className="text-slate-300">—</span>}
                          </Td>
                          <Td className="text-center">
                            {m.name_mismatch
                              ? <input type="checkbox" checked={fixNames.has(m.seq)} onChange={() => toggle(setFixNames)(m.seq)} className="w-4 h-4 accent-amber-600" />
                              : <span className="text-slate-300">—</span>}
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {t.idcard_conflict ? (
                  <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">
                    มี {fmt(t.idcard_conflict)} รายที่ในโปรแกรมมีเลขบัตร 13 หลักอยู่แล้วแต่<b>ไม่ตรง</b>กับรายงาน —
                    ระบบไม่แก้ให้และไม่แสดงตัวเลือก เพราะแปลว่าอาจเป็นคนละคนที่ชื่อเหมือนกัน ต้องตรวจด้วยตาก่อน
                  </div>
                ) : null}
              </Card>
            ) : null}

            {/* ── 4. commit ── */}
            <Card icon={<CheckCircle2 size={17} />} title="บันทึกผล">
              {notYetPaid.length ? (
                <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
                  <b>ไม่บันทึก {fmt(notYetPaid.length)} รายการ ({fmt(sumAmt(notYetPaid))} ฿)</b> — {parsed?.header?.has_voucher_column === false
                    ? <>รายงานการส่งเบิกไม่มีเลขที่ใบสำคัญรับเงินและวันที่โอน · อัปโหลด<u>รายงานการจ่าย</u> (REPORT_02) ของงวดเดียวกันเพื่อบันทึกรับเงิน</>
                    : <>อยู่ในรายงานแต่<u>ยังไม่มีเลขที่ใบสำคัญรับเงินและวันที่โอน</u>
                      {' '}แปลว่า ปกส. รับเรื่องแล้วแต่ยังไม่จ่าย · ระบบจะไม่ตีว่ารับเงินแล้ว รอรายงานงวดที่จ่ายจริงแล้วอัปโหลดอีกครั้ง</>}
                </div>
              ) : null}
              {nothingToDo ? (
                <div className="text-[13px] text-slate-600">ไม่มีรายการที่ต้องบันทึกเพิ่ม</div>
              ) : (
                <div className="space-y-3">
                  <table className="text-[13px] text-slate-700">
                    <tbody>
                      {voucherGroups.map((g) => (
                        <tr key={g.voucher}>
                          <td className="py-0.5 pr-2"><CheckCircle2 size={13} className="text-emerald-600" /></td>
                          <td className="py-0.5 pr-3 font-mono text-[12px] text-slate-600">{g.voucher}</td>
                          <td className="py-0.5 pr-3">โอน {thDate(g.transfer_date)}</td>
                          <td className="py-0.5 pr-3 text-right tabular-nums">{fmt(g.rows.length)} รายการ</td>
                          <td className="py-0.5 text-right tabular-nums font-medium text-slate-900">{fmt(g.amount)} ฿</td>
                        </tr>
                      ))}
                      {voucherGroups.length > 1 ? (
                        <tr className="border-t border-slate-200">
                          <td /><td /><td className="py-1 pr-3 font-medium">รวม</td>
                          <td className="py-1 pr-3 text-right tabular-nums font-medium">{fmt(toWrite.length)} รายการ</td>
                          <td className="py-1 text-right tabular-nums font-semibold text-slate-900">{fmt(sumAmt(toWrite))} ฿</td>
                        </tr>
                      ) : null}
                      {idFixes.length ? (
                        <tr><td className="py-0.5 pr-2"><IdCard size={13} className="text-emerald-600" /></td>
                          <td colSpan={4} className="py-0.5">เติมเลขบัตร {fmt(idFixes.length)} ราย</td></tr>
                      ) : null}
                      {nameFixes.length ? (
                        <tr><td className="py-0.5 pr-2"><AlertTriangle size={13} className="text-amber-600" /></td>
                          <td colSpan={4} className="py-0.5">แก้ชื่อ {fmt(nameFixes.length)} ราย (ทับของเดิม)</td></tr>
                      ) : null}
                    </tbody>
                  </table>
                  <button type="button" disabled={committing || !maySetStatus} onClick={() => setConfirm(true)}
                    title={maySetStatus ? undefined : 'บันทึกได้เฉพาะ GM หรือ Admin'}
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white px-4 py-2 text-[13.5px] font-medium">
                    {committing ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} ยืนยันและบันทึก
                  </button>
                </div>
              )}
              {commitMsg ? (
                <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] text-emerald-800">{commitMsg}</div>
              ) : null}
            </Card>
          </div>

        </>
      ) : null}

      {confirm ? (
        <ConfirmDialog
          title="ยืนยันและบันทึก?"
          message={[
            voucherGroups.length ? `บันทึกรับเงิน ${fmt(toWrite.length)} รายการ ${fmt(sumAmt(toWrite))} ฿ จาก ${voucherGroups.length} ใบสำคัญ` : '',
            idFixes.length ? `เติมเลขบัตร ${fmt(idFixes.length)} ราย` : '',
            nameFixes.length ? `แก้ชื่อทับของเดิม ${fmt(nameFixes.length)} ราย (เก็บชื่อเดิมไว้ในโน้ต)` : '',
          ].filter(Boolean).join(' · ')}
          confirmLabel="บันทึก"
          tone={nameFixes.length ? 'danger' : 'default'}
          onConfirm={commit}
          onCancel={() => setConfirm(null)}
        />
      ) : null}
    </div>
  )
}
